import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { DataTable } from '@/components/ui/data-table';
import { useSetPromotedBlueprintEnabled } from '@/hooks/use-promoted-blueprints';
import { usePermissions } from '@/hooks/use-permissions';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { getActionIntentsColumns } from './action-intents-columns';
import type { WorkflowBlueprint } from '../types';

interface ActionIntentsTableLabels {
  intentHeader?: string;
  subIntentsHeader?: string;
  subIntentsSuffix?: string;
  enabledHeader?: string;
  emptyMessage?: string;
}

export function ActionIntentsTable({
  workflowBlueprints,
  isLoading,
  onRowClick,
  labels
}: {
  workflowBlueprints: WorkflowBlueprint[];
  isLoading: boolean;
  onRowClick: (blueprint: WorkflowBlueprint) => void;
  labels?: ActionIntentsTableLabels;
}) {
  const { can } = usePermissions();
  const canEdit = can('edit', 'promoted-blueprint');
  const setEnabledMutation = useSetPromotedBlueprintEnabled();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleToggleEnabled = async (blueprint: WorkflowBlueprint, enabled: boolean) => {
    const queryKey = ['promoted-blueprints', 'workflow'];
    // Optimistic update
    queryClient.setQueryData<WorkflowBlueprint[]>(queryKey, (prev) =>
      (prev ?? []).map((item) => (item.id === blueprint.id ? { ...item, enabled } : item))
    );
    setPendingId(blueprint.id);
    try {
      await setEnabledMutation.mutateAsync({ id: blueprint.id, track: 'workflow', enabled });
      toast.success(enabled ? 'Intent enabled' : 'Intent disabled');
    } catch (error) {
      // Rollback
      queryClient.setQueryData<WorkflowBlueprint[]>(queryKey, (prev) =>
        (prev ?? []).map((item) => (item.id === blueprint.id ? { ...item, enabled: !enabled } : item))
      );
      toast.error('Failed to update intent');
      console.error('Toggle action intent error:', error);
    } finally {
      setPendingId(null);
      invalidateQueries(queryKey);
    }
  };

  const columns = getActionIntentsColumns({ canEdit, pendingId, onToggleEnabled: handleToggleEnabled, labels });

  return (
    <DataTable
      columns={columns}
      data={workflowBlueprints ?? []}
      isLoading={isLoading}
      enableSorting={true}
      enableFiltering={true}
      enablePagination={true}
      enableColumnVisibility={false}
      enableRowSelection={false}
      pageSize={200}
      pageSizeOptions={[10, 20, 50]}
      emptyMessage={labels?.emptyMessage ?? 'No action intents found.'}
      onRowClick={onRowClick}
    />
  );
}
